/**
 * How a searchlight *moves*, with no encounter in it — the aiming law of
 * AD-017 stripped down to arithmetic.
 *
 * Split out of `searchlights.ts` when that file went past its size ceiling,
 * along the seam it already had. Everything here is a pure function of a seed
 * and a clock, or of a direction and a step: the idle scissor sweep, the slew
 * limiter that keeps a tonne of mirror from teleporting, and the pooled record
 * a beam`s aim is written into. Nothing in it knows what a battery is, let
 * alone what a searchlight post is, which is why every angle below is a unit
 * test rather than a screenshot.
 *
 * `searchlights.ts` next door is the *policy*: which lights exist this frame,
 * which of them are still working, and what each is looking at. It re-exports
 * all of this, so no consumer moved.
 */

import { fnv1a32 } from "@red-opus/engine";

const DEG = Math.PI / 180;

/**
 * Pool ceiling. Well past the 4-6 a mission actually briefs, so a future
 * generator that defends a target properly cannot overrun the buffers, and
 * small enough that the unused instances cost nothing but address space.
 */
export const MAX_SEARCHLIGHTS = 24;

/** Degrees of azimuth either side of a beam's resting bearing. */
export const AZIMUTH_SWEEP_DEG = 55;
/** Seconds for one full azimuth sweep out and back. */
export const AZIMUTH_PERIOD_S = 23;
/** The elevation a beam sweeps about — high enough to be sky, low enough to be a search. */
export const ELEVATION_CENTRE_DEG = 48;
/** Degrees of nod either side of centre, so the beam walks 26 to 70 degrees up. */
export const ELEVATION_SWEEP_DEG = 22;
/** Seconds for one full nod. Coprime with `AZIMUTH_PERIOD_S`, so the pair does not repeat inside 391 s. */
export const ELEVATION_PERIOD_S = 17;

/**
 * How fast the mount can be swung, in degrees a second. A real WWI light was
 * cranked by hand against a great deal of inertia; 40 deg/s is quick for that
 * and still slow enough that the pounce onto a target *reads* as a swing. It
 * is above every rate the idle sweep asks for (`AZIMUTH_SWEEP_DEG` over a
 * quarter of `AZIMUTH_PERIOD_S` peaks at about 15 deg/s), so the limiter is
 * invisible until a target appears.
 */
export const MAX_SLEW_DEG_PER_S = 40;

/** Beam brightness while sweeping. Below the tracking value so a lit aeroplane is a change, not a constant. */
export const IDLE_INTENSITY = 0.55;
/** Brightness the instant a battery lays on, before its guns have ranged in. */
export const ACQUIRING_INTENSITY = 0.78;
/** Brightness once a gun is at `trackingLevel` 2 — the light is steady and the shells are close. */
export const HELD_INTENSITY = 1;

/** A beam's live aim. Mutable and pooled: the collector rewrites these in place every frame. */
export interface SearchlightBeam {
  /** Emplacement position in world metres — `flak.ts` puts every battery at `y = 0`. */
  originX: number;
  originY: number;
  originZ: number;
  /** Unit vector the beam currently points along. Slewed, so it lags the aim below. */
  dirX: number;
  dirY: number;
  dirZ: number;
  /** 0..1, feeding the beam's additive brightness. Zero for a wreck, which is what puts the light out. */
  intensity: number;
  /** True while this beam is on a target the gunnery named. */
  tracking: boolean;
  /**
   * The post has been shot out (AD-017 §11) — its lamp is dark and its mount
   * lies where it fell. The entry stays in the pool because the *hardware* is
   * still there to draw; only the beam has gone.
   */
  wrecked: boolean;
  /** Which way a wrecked mount toppled. Seeded, so a wreck does not re-fall every frame. */
  wreckBearingRad: number;
  /** `fnv1a32` of the battery id and beam index — this beam's sweep, fixed for the sortie. */
  seed: number;
  /** False until the first collection has aimed it, so a new beam starts *at* its sweep instead of slewing up from the pole. */
  aimed: boolean;
}

export function emptySearchlightBeam(): SearchlightBeam {
  return {
    originX: 0,
    originY: 0,
    originZ: 0,
    dirX: 0,
    dirY: 1,
    dirZ: 0,
    intensity: 0,
    tracking: false,
    wrecked: false,
    wreckBearingRad: 0,
    seed: 0,
    aimed: false,
  };
}

/** A unit direction, written in place so aiming a beam allocates nothing. */
export interface AimVector {
  x: number;
  y: number;
  z: number;
}


/** This beam's own sweep, fixed for the sortie by the battery it stands at and which of the pair it is. */
export function beamSeed(batteryId: string, beamIndex: number): number {
  return fnv1a32(`${batteryId}#${beamIndex}`);
}

/**
 * A stable `[0, 1)` draw from a beam's seed. `stream` separates the four
 * quantities a sweep needs so they cannot correlate — two beams whose seeds
 * happen to be close still get unrelated phases.
 */
export function seededUnit(seed: number, stream: number): number {
  const mixed = Math.imul(seed ^ Math.imul(stream + 1, 0x9e3779b1), 0x85ebca6b) >>> 0;
  return ((mixed ^ (mixed >>> 15)) >>> 0) / 0x1_0000_0000;
}

/**
 * The direction a beam would point at `timeS` if nothing were in the sky:
 * azimuth riding one sine about a resting bearing, elevation riding another.
 *
 * World axes are `time-of-day.ts`'s — `+X` east, `−Z` north, `+Y` up — and the
 * azimuth-to-bearing mapping is `celestialDirection`'s, so a beam and the moon
 * agree about which way north is.
 *
 * Pure in `(seed, timeS)`, which is what makes the sweep testable and what
 * makes two runs of one mission identical.
 */
export function sweepAim(seed: number, timeS: number, out: AimVector): AimVector {
  const restingBearing = seededUnit(seed, 0) * 2 * Math.PI;
  const azimuthPhase = seededUnit(seed, 1) * 2 * Math.PI;
  const elevationPhase = seededUnit(seed, 2) * 2 * Math.PI;
  // A +/-15% stretch on both periods, so no two beams in a mission ever lock
  // into the same figure however close their phases landed.
  const stretch = 0.85 + seededUnit(seed, 3) * 0.3;

  const azimuth =
    restingBearing +
    AZIMUTH_SWEEP_DEG * DEG * Math.sin((2 * Math.PI * timeS) / (AZIMUTH_PERIOD_S * stretch) + azimuthPhase);
  const elevation =
    (ELEVATION_CENTRE_DEG +
      ELEVATION_SWEEP_DEG * Math.sin((2 * Math.PI * timeS) / (ELEVATION_PERIOD_S * stretch) + elevationPhase)) *
    DEG;

  const horizontal = Math.cos(elevation);
  out.x = horizontal * Math.sin(azimuth);
  out.y = Math.sin(elevation);
  out.z = -horizontal * Math.cos(azimuth);
  return out;
}

/**
 * Rotates `beam`'s direction towards `aim` by at most `MAX_SLEW_DEG_PER_S · dt`,
 * along the great circle between them — a mount swings, it does not cross-fade.
 *
 * Spherical rather than a normalised lerp because the limit is stated in
 * degrees and only a slerp actually travels the angle it is asked for; a
 * normalised lerp at `t = maxRad/theta` undershoots, by more the wider the
 * swing, which would make the pounce onto a distant target quietly slower than
 * the constant says.
 */
export function slewBeam(beam: SearchlightBeam, aim: AimVector, dt: number): void {
  const dot = Math.min(1, Math.max(-1, beam.dirX * aim.x + beam.dirY * aim.y + beam.dirZ * aim.z));
  const theta = Math.acos(dot);
  const maxStep = MAX_SLEW_DEG_PER_S * DEG * Math.max(0, dt);
  // Already there, or close enough that the sine below would divide by nothing.
  if (theta <= maxStep || theta < 1e-6) {
    beam.dirX = aim.x;
    beam.dirY = aim.y;
    beam.dirZ = aim.z;
    return;
  }
  const sinTheta = Math.sin(theta);
  const t = maxStep / theta;
  const from = Math.sin((1 - t) * theta) / sinTheta;
  const to = Math.sin(t * theta) / sinTheta;
  const x = beam.dirX * from + aim.x * to;
  const y = beam.dirY * from + aim.y * to;
  const z = beam.dirZ * from + aim.z * to;
  const length = Math.hypot(x, y, z) || 1;
  beam.dirX = x / length;
  beam.dirY = y / length;
  beam.dirZ = z / length;
}

